/**
 * Legacy keys from older `commercial_profile.metadata` (camelCase) → catalog `slug`.
 * Must stay aligned with backend `tenant-catalog-sync.config.ts`.
 */
const LEGACY_PRODUCT_FLAG_TO_SLUG: Record<string, string> = {
  agenticPd: 'agentic-pd',
  agenticKyc: 'agentic-kyc',
  aadhaarKyc: 'aadhaar-kyc',
  panKyc: 'pan-kyc',
};

const LEGACY_SERVICE_FLAG_TO_SLUG: Record<string, string> = {
  homeLoan: 'home-loan',
  goldLoan: 'gold-loan',
  businessLoan: 'business-loan',
  lap: 'lap',
  autoLoan: 'auto-loan',
  personalLoan: 'personal-loan',
};

/** Catalog / DB slug for Agentic KYC — aligned with `tenant-catalog-sync.config.ts`. */
export const PRODUCT_SLUG_AGENTIC_KYC = 'agentic-kyc';

/** Catalog / DB slug for Agentic PD — aligned with `tenant-catalog-sync.config.ts`. */
export const PRODUCT_SLUG_AGENTIC_PD = 'agentic-pd';

export interface CatalogRow {
  id: number;
  name: string;
  slug: string;
}

/** Initialize `{ [slug]: false }` for every catalog row. */
export function emptySelectionMap(catalog: CatalogRow[]): Record<string, boolean> {
  const m: Record<string, boolean> = {};
  for (const row of catalog) {
    m[row.slug] = false;
  }
  return m;
}

/** JSON for `commercial_metadata` — uses **slug keys** so new DB products/services work without UI deploys. */
export function buildCommercialMetadataJson(
  productsCatalog: CatalogRow[],
  servicesCatalog: CatalogRow[],
  productSelection: Record<string, boolean>,
  serviceSelection: Record<string, boolean>,
  productCharges: Record<string, string | number | null | undefined> = {},
): string {
  const products: Record<string, boolean> = {};
  const charges: Record<string, number> = {};
  for (const p of productsCatalog) {
    products[p.slug] = !!productSelection[p.slug];
    // Only carry a per-PD charge for selected products with a valid amount.
    if (productSelection[p.slug]) {
      const raw = productCharges[p.slug];
      const num = typeof raw === 'string' ? Number.parseFloat(raw) : raw;
      if (num != null && Number.isFinite(num)) charges[p.slug] = num;
    }
  }
  const loanServices: Record<string, boolean> = {};
  for (const s of servicesCatalog) {
    loanServices[s.slug] = !!serviceSelection[s.slug];
  }
  return JSON.stringify({ products, loanServices, productCharges: charges });
}

function applyMetadataFlags(
  source: Record<string, boolean> | undefined,
  legacyMap: Record<string, string>,
  selection: Record<string, boolean>,
): void {
  if (!source || typeof source !== 'object') {
    return;
  }
  for (const [key, val] of Object.entries(source)) {
    const slug = legacyMap[key] ?? (/^[a-z0-9]+(-[a-z0-9]+)*$/.test(key) ? key : null);
    if (slug && Object.hasOwn(selection, slug)) {
      selection[slug] = !!val;
    }
  }
}

/** Merge legacy metadata + optional slug-keyed metadata into selection maps (only known slugs). */
export function applyCommercialMetadataToSelections(
  metadata: unknown,
  productSelection: Record<string, boolean>,
  serviceSelection: Record<string, boolean>,
): void {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return;
  }
  const m = metadata as {
    products?: Record<string, boolean>;
    loanServices?: Record<string, boolean>;
  };
  applyMetadataFlags(m.products, LEGACY_PRODUCT_FLAG_TO_SLUG, productSelection);
  applyMetadataFlags(m.loanServices, LEGACY_SERVICE_FLAG_TO_SLUG, serviceSelection);
}

export function hasAnyProductSelected(
  productsCatalog: CatalogRow[],
  productSelection: Record<string, boolean>,
): boolean {
  return productsCatalog.some((p) => !!productSelection[p.slug]);
}

export function hasAnyServiceSelected(
  servicesCatalog: CatalogRow[],
  serviceSelection: Record<string, boolean>,
): boolean {
  return servicesCatalog.some((s) => !!serviceSelection[s.slug]);
}

/**
 * Normalizes `GET /product/dropdown` / `GET /services/dropdown` envelope `data` into catalog rows.
 * Returns `null` if the response is not a successful catalog payload.
 */
export function parseCatalogFromDropdownEnvelope(env: {
  status?: boolean;
  data?: unknown;
}): CatalogRow[] | null {
  if (env?.status !== true) {
    return null;
  }
  const raw = env.data;
  if (!Array.isArray(raw)) {
    return null;
  }
  const out: CatalogRow[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const r = item as Partial<CatalogRow>;
    const slug = typeof r.slug === 'string' ? r.slug.trim() : '';
    if (!slug) {
      continue;
    }
    const name =
      typeof r.name === 'string' && r.name.trim() ? r.name.trim() : slug;
    const id = typeof r.id === 'number' ? r.id : 0;
    out.push({ id, name, slug });
  }
  return out;
}
